import { useContentfulImages } from '@snapchat/mw-contentful-client';
import type { MosaicFilterProps, MosaicTileProps } from '@snapchat/snap-design-system-marketing';
import { Mosaic as MosaicSDS } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logUserEvent } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import { getMediaProps } from '../Video/Video';
import type { MosaicTilesDataProps } from './types';

export const Mosaic: FC<MosaicTilesDataProps> = props => {
  const elementLocation = useContext(ElementLocation).elementLocation;
  const { getBestBgImgSrc, getImageSources } = useContentfulImages();

  const { tilesCollection, columns, mobileColumns, filtersCollection } = props;

  const tiles = tilesCollection.items.map(
    ({ video, preview, thumbnail, filtersCollection, analytics, ...item }) => {
      const poster = getImageSources(thumbnail.url, { image: { width: 768 } });
      const label = analytics?.label ?? item.sys.id;
      const component = 'MosaicTile';

      return {
        ...item,
        onClose: () =>
          logUserEvent({
            eventAction: UserAction.Close,
            eventCategory: component,
            eventLabel: label,
            context: { elementText: label, elementLocation },
          }),
        onOpen: () =>
          logUserEvent({
            eventAction: UserAction.Open,
            eventCategory: component,
            eventLabel: label,
            context: { elementText: label, elementLocation },
          }),
        onEnter: () =>
          logUserEvent({
            eventAction: UserAction.Enter,
            eventCategory: component,
            eventLabel: label,
            context: { elementText: label, elementLocation },
          }),
        onLeave: () =>
          logUserEvent({
            eventAction: UserAction.Leave,
            eventCategory: component,
            eventLabel: label,
            context: { elementText: label, elementLocation },
          }),
        filters: filtersCollection?.items?.map(i => i.sys.id),
        poster: {
          src: poster?.default,
          srcSet: poster?.defaultSrcSet,
        },
        video: video ? getMediaProps(video, getBestBgImgSrc) : undefined,
        preview: preview ? getMediaProps(preview, getBestBgImgSrc) : undefined,
      } as MosaicTileProps;
    }
  );

  const filters = filtersCollection.items?.map(
    ({ sys, isAll, ...filter }) => ({ id: isAll ? '' : sys.id, ...filter } as MosaicFilterProps)
  );

  return (
    <MosaicSDS tiles={tiles} columns={columns} mobileColumns={mobileColumns} filters={filters} />
  );
};
Mosaic.displayName = 'Mosaic';
